/**
 * Журнал правок договора: кто, когда, что изменил («было → стало» по изменённым полям), итог
 * синхронизации с Bitrix, откат правки (право `contract_edit`). Свёрнут по умолчанию.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { WriteOutcome } from "@/components/contract/ui";
import { type ContractChange, contractOptionsQuery, rollbackChange, useContractRefresh, writeError } from "@/lib/contract";
import { btn } from "@/lib/panelBtn";

const OPS: Record<string, string> = {
  create: "создан",
  update: "поля",
  product_add: "добавлен товар",
  product_update: "правка товара",
  product_delete: "удалён",
  product_restore: "восстановлен",
  rollback: "откат",
  sync: "синхронизация",
  passport_view: "просмотр паспорта",
};
const NO_ROLLBACK = ["create", "sync", "passport_view"];

const DOGOVOR_LABELS: Record<string, string> = {
  customerName: "ФИО",
  customerPhone: "Телефон",
  customerEmail: "Почта",
  dogovorMessengerStatusID: "Мессенджер",
  rentStart: "Начало проката",
  rentEnd: "Конец проката",
  daysPayed: "Оплачено дней",
  locationID: "Салон",
  dogovorCategoryID: "Категория",
  deliveryStart: "Доставка",
  deliveryStartAdress: "Доставка, адрес",
  deliveryEnd: "Забор",
  deliveryEndAdress: "Забор, адрес",
  pass: "Паспорт",
};
const ROW_LABELS: Record<string, string> = {
  productName: "Название",
  serial: "Серия",
  priceAccount: "Цена",
  priceDay: "Дней",
  isSale: "Продажа",
  productComment: "Комментарий",
  podwiv: "Подшив",
  isDeleted: "Удалён",
};

type Snap = { dogovor?: Record<string, unknown>; products?: Record<string, Record<string, unknown>> };
const snap = (v: unknown): Snap => (v && typeof v === "object" ? (v as Snap) : {});
const s = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : String(v));

type Names = Record<string, Map<string, string>>;

/** «было → стало» строками: только поля, что различаются; пустое «до» (создание) тоже показываем. */
function diffLines(c: ContractChange, names: Names): string[] {
  const b = snap(c.before);
  const a = snap(c.after);
  const out: string[] = [];
  const val = (field: string, v: unknown) => names[field]?.get(String(v)) ?? s(v);
  for (const [f, label] of Object.entries(DOGOVOR_LABELS)) {
    const x = b.dogovor?.[f];
    const y = a.dogovor?.[f];
    if (s(x) !== s(y)) out.push(`${label}: ${val(f, x)} → ${val(f, y)}`);
  }
  for (const [id, after] of Object.entries(a.products ?? {})) {
    const before = b.products?.[id];
    const name = after.productName ? `${s(after.productName)}${after.serial ? ` · ${s(after.serial)}` : ""}` : `строка ${id}`;
    if (!before) {
      out.push(`${name}: добавлена строка`);
      continue;
    }
    for (const [f, label] of Object.entries(ROW_LABELS)) {
      if (s(before[f]) === s(after[f])) continue;
      if (f === "isDeleted") out.push(`${name}: ${s(after[f]) === "1" ? "удалена" : "восстановлена"}`);
      else out.push(`${name}: ${label}: ${s(before[f])} → ${s(after[f])}`);
    }
  }
  return out;
}

function Row({ c, names, dealId, dogovorId, chatId, can }: { c: ContractChange; names: Names; dealId: number; dogovorId: number; chatId: number | null; can: boolean }) {
  const refresh = useContractRefresh(dealId, dogovorId, chatId);
  const [ask, setAsk] = useState(false);
  const m = useMutation({
    mutationFn: () => rollbackChange(dogovorId, c.id, chatId),
    onSettled: () => {
      setAsk(false);
      refresh();
    },
  });
  const lines = diffLines(c, names);
  const op = c.op === "rollback" && c.rollback_of ? `откат правки ${c.rollback_of}` : (OPS[c.op] ?? c.op);
  const canRollback = can && !c.rolled_back_by && !c.error && !NO_ROLLBACK.includes(c.op);
  return (
    <li className="flex flex-col gap-0.5 rounded-[var(--radius-ui)] border border-border bg-bg p-2" data-annot={`правка ${c.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-mono text-fg-muted">#{c.id}</span>
        <span className="tabular-nums text-fg-muted">{c.at ? c.at.replace("T", " ").slice(0, 16) : "—"}</span>
        <span>{c.user_name || "—"}</span>
        <span className="font-medium">{op}</span>
        {c.sync === "ok" && <span className="text-ok">синхр.</span>}
        {c.sync === "error" && <span className="text-err">синхр.: ошибка</span>}
      </div>
      {lines.map((l, i) => (
        <div key={i} className="break-words text-fg-muted">
          {l}
        </div>
      ))}
      {c.comment && <div className="break-words">Причина: «{c.comment}»</div>}
      {c.sync === "ok" && c.sync_detail?.includes("сверено") && <div className="break-words text-ok">{c.sync_detail}</div>}
      {c.rolled_back_by && <div className="text-warn">откачена правкой {c.rolled_back_by}</div>}
      {c.error && <div className="text-err">{c.error}</div>}
      {c.sync === "error" && c.sync_detail && <div className="break-words text-err">{c.sync_detail}</div>}
      {m.isError && <div className="whitespace-pre-wrap text-err">{writeError(m.error)}</div>}
      {m.data && <WriteOutcome r={m.data} ok={`Правка ${c.id} откачена`} onClose={m.reset} />}
      {canRollback &&
        (ask ? (
          <div className="flex flex-wrap items-center gap-2">
            <span>Откатить правку {c.id}? Запишется в боевую CRM.</span>
            <button type="button" disabled={m.isPending} onClick={() => m.mutate()} className={btn.primary}>
              {m.isPending ? "Откатываю…" : "Да, откатить"}
            </button>
            <button type="button" disabled={m.isPending} onClick={() => setAsk(false)} className={btn.quiet}>
              Нет
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => { m.reset(); setAsk(true); }} className="self-start text-[12px] text-accent-text hover:underline">
            Откатить
          </button>
        ))}
    </li>
  );
}

/** `can` — откат доступен (право, запись включена, договор этой сделки): решает карточка договора. */
export function ChangesLog({ dealId, dogovorId, chatId, changes, can }: { dealId: number; dogovorId: number; chatId: number | null; changes: ContractChange[]; can: boolean }) {
  const [open, setOpen] = useState(false);
  const opts = useQuery({ ...contractOptionsQuery(), enabled: open });
  const toMap = (l: { id: number; name: string }[] | undefined) => new Map((l ?? []).map((o) => [String(o.id), o.name]));
  const names: Names = {
    locationID: toMap(opts.data?.locations),
    dogovorMessengerStatusID: toMap(opts.data?.messengers),
    dogovorCategoryID: toMap(opts.data?.categories),
  };
  return (
    <div className="flex flex-col gap-1.5" data-annot={`журнал правок договора ${dogovorId}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="self-start text-[12px] text-accent-text hover:underline">
        {open ? "Скрыть журнал правок" : `Журнал правок (${changes.length})`}
      </button>
      {open && (
        <ul className="flex flex-col gap-1.5 text-[12px]">
          {changes.length === 0 && <li className="text-fg-muted">Правок нет.</li>}
          {changes.map((c) => (
            <Row key={c.id} c={c} names={names} dealId={dealId} dogovorId={dogovorId} chatId={chatId} can={can} />
          ))}
        </ul>
      )}
    </div>
  );
}
